import React, { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Slide,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  X,
  CircleDollarSign,
  ShoppingBag,
  Package,
  Users,
  FileQuestion,
  Bell,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useNotifications } from "./NotificationContext";
import { formatNotificationTime } from "../../../dummyData";

const NotificationPopup = () => {
  const { showPopup, currentPopup, markAsRead, isPopupSilenced } =
    useNotifications();
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Only show the popup if notifications are not silenced
    if (showPopup && !isPopupSilenced) {
      setVisible(true);
    } else {
      // Add a small delay before hiding to allow the exit animation
      const timeout = setTimeout(() => {
        setVisible(false);
      }, 300);

      return () => clearTimeout(timeout);
    }
  }, [showPopup, isPopupSilenced]);

  if (!visible || !currentPopup) return null;

  const handleClose = () => {
    setVisible(false);
  };

  const handleClick = () => {
    if (currentPopup && currentPopup.link) {
      markAsRead(currentPopup.id);
      navigate(currentPopup.link);
    }
    setVisible(false);
  };

  // Get icon based on notification type
  const getNotificationIcon = type => {
    switch (type) {
      case "order":
        return <ShoppingBag size={20} color="#5652B0" />;
      case "payment":
        return <CircleDollarSign size={20} color="#22c55e" />;
      case "inventory":
        return <Package size={20} color="#f59e0b" />;
      case "customer":
        return <Users size={20} color="#5652B0" />;
      case "request":
        return <FileQuestion size={20} color="#E96479" />;
      default:
        return <Bell size={20} color="#6B7280" />;
    }
  };

  return (
    <Slide direction="left" in={visible} mountOnEnter unmountOnExit>
      <Paper
        elevation={3}
        sx={{
          position: "fixed",
          top: "5rem",
          right: "1rem",
          width: "320px",
          borderRadius: "12px",
          overflow: "hidden",
          border: "1px solid rgba(229, 231, 235, 0.8)",
          zIndex: 1400,
          cursor: "pointer",
          "&:hover": {
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
          },
        }}
        onClick={handleClick}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: "1px solid rgba(229, 231, 235, 0.8)",
            backgroundColor: "rgba(86, 82, 176, 0.04)",
            p: 1.5,
          }}>
          <Typography
            variant="subtitle2"
            className="outfit-semibold"
            sx={{
              color: "#374151",
              fontSize: "0.875rem",
            }}>
            New Notification
          </Typography>
          <Tooltip title="Close">
            <IconButton
              size="small"
              onClick={e => {
                e.stopPropagation();
                handleClose();
              }}
              sx={{ p: 0.5 }}>
              <X size={16} color="#6B7280" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "flex-start",
          }}>
          <Avatar
            sx={{
              bgcolor: "rgba(86, 82, 176, 0.1)",
              width: 38,
              height: 38,
              mr: 1.5,
            }}>
            {getNotificationIcon(currentPopup.type)}
          </Avatar>

          <Box>
            <Typography
              variant="body2"
              className="outfit-semibold"
              sx={{
                color: "#374151",
                mb: 0.5,
                fontSize: "0.875rem",
              }}>
              {currentPopup.title}
            </Typography>

            <Typography
              variant="body2"
              className="outfit-regular"
              sx={{
                color: "#6B7280",
                fontSize: "0.8125rem",
                mb: 1,
              }}>
              {currentPopup.message}
            </Typography>

            <Typography
              variant="caption"
              sx={{
                color: "#9CA3AF",
                fontSize: "0.75rem",
              }}>
              {formatNotificationTime(currentPopup.timestamp)}
            </Typography>
          </Box>
        </Box>
      </Paper>
    </Slide>
  );
};

export default NotificationPopup;
